import React, { useCallback } from 'react';
import clsx from 'clsx';
import type { GuestDetail, Gender } from '@camp67/model';
import type { GuestCardMessages } from '../types';

interface GuestCardExpandableProps {
  guest: GuestDetail;
  index: number;
  isExpanded: boolean;
  isPrimaryContact: boolean;
  onToggle: () => void;
  onChange: (id: string, detail: Partial<GuestDetail>) => void;
  messages: GuestCardMessages;
}

/**
 * Expandable guest card for entering guest details.
 * Collapsed state shows summary, expanded state shows full form.
 */
export const GuestCardExpandable: React.FC<GuestCardExpandableProps> = ({
  guest,
  index,
  isExpanded,
  isPrimaryContact,
  onToggle,
  onChange,
  messages,
}) => {
  const isComplete = guest.name && guest.age > 0 && guest.gender !== 'none';

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(guest.id, { name: e.target.value });
    },
    [guest.id, onChange],
  );

  const handleAgeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const age = parseInt(e.target.value, 10);
      if (!isNaN(age) && age >= 0 && age <= 120) {
        onChange(guest.id, { age });
      }
    },
    [guest.id, onChange],
  );

  const handleGenderChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(guest.id, { gender: e.target.value as Gender });
    },
    [guest.id, onChange],
  );

  return (
    <div
      className={clsx(
        'border rounded-xl transition-all duration-200',
        isExpanded ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white',
        isComplete && !isExpanded && 'border-emerald-200',
      )}
    >
      {/* Header - always visible */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          {/* Guest number badge */}
          <div
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
              isComplete
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-gray-100 text-gray-500',
            )}
          >
            {isComplete ? <CheckIcon className="w-4 h-4" /> : index + 1}
          </div>

          {/* Guest info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                {guest.name || `Guest ${index + 1}`}
              </span>
              {isPrimaryContact && (
                <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                  Primary
                </span>
              )}
            </div>
            {!isExpanded && isComplete && (
              <p className="text-sm text-gray-500">
                {guest.age} years, {formatGender(guest.gender, messages)}
              </p>
            )}
          </div>
        </div>

        {/* Expand/collapse icon */}
        <ChevronIcon
          className={clsx(
            'w-5 h-5 text-gray-400 transition-transform duration-200',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Name field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {messages.enterName}
            </label>
            <input
              type="text"
              value={guest.name}
              onChange={handleNameChange}
              placeholder="Full name"
              className={clsx(
                'w-full px-3 py-2.5 rounded-lg border',
                'text-gray-900 placeholder-gray-400',
                'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent',
                'transition-colors duration-150',
                guest.name ? 'border-gray-200 bg-white' : 'border-gray-300 bg-gray-50',
              )}
            />
          </div>

          {/* Age and Gender row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Age field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {messages.enterAge}
              </label>
              <input
                type="number"
                min="0"
                max="120"
                value={guest.age || ''}
                onChange={handleAgeChange}
                placeholder="Age"
                className={clsx(
                  'w-full px-3 py-2.5 rounded-lg border',
                  'text-gray-900 placeholder-gray-400',
                  'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent',
                  'transition-colors duration-150',
                  guest.age > 0 ? 'border-gray-200 bg-white' : 'border-gray-300 bg-gray-50',
                )}
              />
            </div>

            {/* Gender field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {messages.selectGender}
              </label>
              <select
                value={guest.gender}
                onChange={handleGenderChange}
                className={clsx(
                  'w-full px-3 py-2.5 rounded-lg border',
                  'text-gray-900',
                  'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent',
                  'transition-colors duration-150 appearance-none bg-white',
                  guest.gender !== 'none'
                    ? 'border-gray-200'
                    : 'border-gray-300 text-gray-400',
                )}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem',
                }}
              >
                <option value="none" disabled>
                  Select...
                </option>
                <option value="male">{messages.genderMale}</option>
                <option value="female">{messages.genderFemale}</option>
                <option value="non-binary">{messages.genderNonBinary}</option>
                <option value="prefer-not-to-say">{messages.genderPreferNotToSay}</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format gender for display
function formatGender(gender: Gender, messages: GuestCardMessages): string {
  switch (gender) {
    case 'male':
      return messages.genderMale;
    case 'female':
      return messages.genderFemale;
    case 'non-binary':
      return messages.genderNonBinary;
    case 'prefer-not-to-say':
      return messages.genderPreferNotToSay;
    default:
      return '';
  }
}

// Icon components
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export default GuestCardExpandable;
